<template>
  <dropdown ref="selectButtonDropdown" title="Status" class="sbwam" placement="bottom">
    <dd-button
      :is-disabled="isDisabled"
      :color="selectedColor"
      :is-submitting="false"
      :size="size"
      type="flat"
      class="sbwam__button"
    >
      <div class="sbwam__button-content">
        <!-- PREFIX ICON -->
        <slot name="prefix" class="prefix" />
        
        <!-- SELECTED VALUE -->
        <slot v-if="selectedStatus" :item="selectedStatus" name="selectedValue">
          <span class="value">{{ selectedStatus.name }}</span>
          <avatar :person="selectedStatusAssignee" :size="19" />
        </slot>

        <!-- ARROW -->
        <span v-if="!isDisabled" class="sbwam__button-content-arrow">
          <i class="ddi ddi-caret-down" />
        </span>
      </div>
    </dd-button>
    <template #content>
      <div>
        <ul>
          <li v-for="status in statuses" :key="`${status.name}-${status.id}`" class="special" @click="handleSelect(status.id)">
            <span :class="{'selected' : isSelected(status)}">
              <chicklet
                :color="status.color"
                :size="$screen.isMobile($mq) ? 'medium' : 'small'"
                type="flat"
                is-uppercase
              >
                <span>{{ status.name }}</span>
              </chicklet>
              <i class="ddi ddi-arrow-right"/>
              <div class="job-info">
                <span>{{ getJobNameMandatedForStatus(status) }}</span>
                <avatar :person="getAssigneeMandatedForStatus(status)" :size="19" />
              </div>
            </span>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { IIDNamePairWithColor } from '@/types/data-objects/global'
import {
  IJobAssigneeMandate,
  IPersonWithEmail,
  IStatusForSelection,
} from '@/api/types/response.types'
import { IJobInfo } from '@/types/data-objects/items'

/* API */

export default defineComponent({
  name: 'SelectButtonWithAssigneeMandates',
  props: {
    modelValue: {
      type: [String, Number, Object, Array],
      default: null,
    },
    statuses: {
      type: Array as () => IStatusForSelection[],
      default: (): IStatusForSelection[] => [],
    },
    jobInfo: {
      type: Object as () => IJobInfo,
      required: true,
    },
    size: {
      type: String,
      default: 'small',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      isOpen: false,
      internalValue: 0,
    }
  },

  computed: {
    // There could be a case where the current assignee IS NOT the mandated assignee
    // (e.g. job rule updated, jobs were turned off before and it was a different assignee).
    // So, only use the assignee mandate if a new status has been selected.
    selectedStatusAssignee(): IPersonWithEmail {
      if (this.selectedStatus.id === this.jobInfo.currentStatus.id) {
        return this.jobInfo.currentAssignee
      }
      return this.getAssigneeMandatedForStatus(this.selectedStatus)
    },
    selectedStatus(): IIDNamePairWithColor {
      if (!this.internalValue) {
        return this.statuses[0]
      }
      return this.statuses.find(o => o.id === this.internalValue)
    },
    selectedColor(): string {
      return this.getColor(this.selectedStatus)
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.internalValue = newVal
      },
    },
  },

  methods: {
    getAssigneeMandatedForStatus(
      status: IStatusForSelection,
    ): IPersonWithEmail {
      const mandate = this.getMandateForStatus(status)
      return mandate
        ? this.jobInfo.jobAppointees.find(ja => ja.job.id === mandate.jobID)
            .appointee
        : null
    },

    getMandateForStatus(status: IStatusForSelection): IJobAssigneeMandate {
      return this.jobInfo.assigneeMandates.find(am => am.statusID === status.id)
    },

    getJobNameMandatedForStatus(status: IStatusForSelection): string {
      const mandate = this.getMandateForStatus(status)
      return mandate
        ? this.jobInfo.jobAppointees.find(ja => ja.job.id === mandate.jobID).job
            .name
        : ''
    },

    getColor(value: IIDNamePairWithColor) {
      return value ? value.color : 'grey'
    },

    handleSelect(value: number) {
      if (this.internalValue === value) {
        return
      }
      this.internalValue = value
      this.$emit('update:modelValue', value)
      // @ts-ignore
      this.$refs.selectButtonDropdown.handleClose()
    },
    isSelected(item: any) {
      if (this.selectedStatus) {
        return item.id === this.selectedStatus.id
      }
      return false
    },
  },
})
</script>

<style lang="scss">
.sbwam {
  position: relative;

  &__button {
    max-width: 100%;
    display: flex;
    align-items: center;
    padding: 0 8px;

    > span {
      width: 100%;
    }

    .prefix-icon {
      width: 16px;
      height: 16px;
    }

    .prefix {
      margin-right: 4px;
    }

    .value {
      @include single-line-ellipsis;
      display: inline-block;
      @include lettering--h5;
      color: inherit;
      line-height: 1;
    }
  }

  &__button-content {
    display: flex;
    align-items: center;
    width: 100%;

    .avatar {
      margin-left: 6px;
    }
  }

  &__button-content-arrow {
    padding-left: 4px;

    .ddi {
      font-size: $dd2-size--dropdown-caret;
      line-height: 14px;
    }
  }

  /* Dropdown override for showing arrow/avatar stuff on assignee mandates */
  .dropdown__popper,
  .action-sheet-body {
    li {
      > span {
        display: flex;
        align-items: center;
        @include flex-row-horizontal-spacing(6px);

        .ddi-arrow-right {
          color: var(--color-text-third);
          font-size: 10px;
        }
      }

      .job-info {
        border-radius: 8px;
        padding: 1px 4px;
        border: $border--solid-light;
        background-color: var(--color-background-primary);
        @include lettering--p-sm;
        display: flex;
        align-items: center;
        @include flex-row-horizontal-spacing(6px);
      }
    }
  }
}
</style>
